<template>
    <info-widget :collapsible="true" :is-expanded="expanded" @toggle_expand="$emit('toggle_expand')">
        <template v-slot:title>Onboarding</template>

        <slot>
            <!-- Current Onboarding Application(s) display -->
            <div class="p-2" v-if="onboardingApplicationId && employee">
                <p v-if="hasBouncedEmail">
                    <alert-box type="caution">
                        <strong>Bounced Email:</strong> Onboarding cannot proceed because email cannot be delivered.
                        <a v-if="canEditEmail" href @click.prevent="openEditEmailModal">Update this user’s email address to re-try.</a>
                    </alert-box>
                </p>

                <p v-if="!employee.approved_on && employee.onboarding_error_message">
                    <alert-box type="error">
                        There was a problem with onboarding this employee: {{ employee.onboarding_error_message }}
                    </alert-box>
                </p>

                <p v-if="employee.approved_on && !employee.onboarded_on">
                    <alert-box type="info">
                        Sending data to payroll system. This may take a few minutes.
                    </alert-box>
                </p>

                <alert-box type="info" class="mb-4" v-if="!userHasAccess">
                    This employee's onboarding is being managed by another user. Contact your Payroll Administrator for more information.
                </alert-box>

                <div class="m-0">
                    <div class="text-semibold" v-for="job in employee.jobs" :key="job.id">{{ job.position.terminal_value }}</div>
                    <div>
                        Expected Start Date: {{ $filters.date(employee.date_hired) }}
                    </div>
                    <div class="pt-1" v-if="employee.status == 'canceled'">
                        <span class="text-danger-dark text-bold">Canceled:</span>
                        {{ $filters.date(employee.canceled_on) }} by {{ employee.canceled_by.email }}
                    </div>
                </div>

                <button-row stacked class="mt-1 mb-0" v-if="userHasAccess && employee.status != 'canceled'">
                    <button class="btn btn-outline-primary" v-if="employee.status == 'started'" @click.prevent="$emit('resend-activation-email')">
                        Re-Send Invitation
                    </button>
                    <button class="btn btn-primary" @click.prevent="startApproval()" v-if="((employee.status == 'needs-manager-approval' && !employee.approved_on) || (employee.status == 'hired' && !employee.approved_on)) && !approvalStarted">
                        Start Approval
                    </button>
                    <button class="btn btn-primary" @click.prevent="$emit('start-approval')" v-if="((employee.status == 'needs-manager-approval' && !employee.approved_on) || (employee.status == 'hired' && !employee.approved_on)) && approvalStarted && !employee.onboarding_error_message">
                        Continue Approval
                    </button>
                    <button class="btn btn-primary" @click.prevent="$refs.approvalModal.open()" v-if="((employee.status == 'needs-manager-approval' && !employee.approved_on) || (employee.status == 'hired' && !employee.approved_on)) && approvalStarted && employee.onboarding_error_message">
                        Retry Approval
                    </button>
                    <button class="btn btn-outline-primary btn-block mb-1" @click.prevent="cancelOnboarding()" v-if="((employee && ['canceled', 'hired'].indexOf(employee.status) < 0) && !employee.approved_on) || (employee.status == 'hired' && !employee.approved_on)">
                        Cancel Onboarding
                    </button>
                </button-row>
            </div>

            <!-- Completed Onboarding Applications display -->
            <div class="p-0" v-if="employee.is_employee">

                <div v-if="onboardingApplications.length">
                    <section class="table-section">
                        <div class="table-section-titlebar">
                            <h5 class="table-section-title">Completed Onboarding Applications <span class="text-regular">({{ onboardingApplications.length }})</span></h5>
                        </div>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="cw-toggle"></th>
                                    <th>{{ client.position_label_pretty }}</th>
                                    <th class="cw-date text-right">Approved</th>
                                </tr>
                            </thead>
                            <tbody v-for="app in onboardingApplications" :key="app.id">
                                <tr>
                                    <td class="cw-toggle position-relative">
                                        <a href class="widget-toggle-wrapper collapse-trigger" role="button" :class="{'is-expanded': expandedOnboardingApplications[app.id]}" @click.prevent="expandedOnboardingApplications[app.id] = !expandedOnboardingApplications[app.id]">
                                            <collapse-toggle-button />
                                        </a>
                                    </td>
                                    <td class="truncate-wrapper">
                                        <div class="truncate-content" v-for="job in app.jobs" :key="job.id">{{ job.position.terminal_value }}</div>
                                    </td>
                                    <td class="text-right">
                                        <span>{{ $filters.dateshort(app.onboarded_on) }}</span>
                                    </td>
                                </tr>

                                <tr class="child no-transition" v-show="expandedOnboardingApplications[app.id]">
                                    <td colspan="3">
                                        <div class="card card-body item-details">
                                            <section class="item-details-section">
                                                <p>
                                                    <span class="d-block">
                                                        Approval Date: {{ $filters.date(app.onboarded_on) }}
                                                    </span>
                                                    <span class="d-block">
                                                        Approver: {{ app.approved_by ? app.approved_by.email : 'N/A' }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    Employee Information
                                                </h5>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Employee Name:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.full_name }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Email Address:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.email }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Expected Start Date:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ $filters.date(app.date_hired) }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        ACA Employment Category:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.aca_category }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Resident Address:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.address_1 }}
                                                    </span>
                                                    <span class="d-block" v-if="app.address_2">
                                                        {{ app.address_2 }}
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.city }}, {{ app.state }} {{ app.postal_code }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Mailing Address:
                                                    </span>
                                                    <template v-if="app.use_mailing_address">
                                                        <span class="d-block">
                                                            {{ app.mailing_address_1 }}
                                                        </span>
                                                        <span class="d-block" v-if="app.mailing_address_2">
                                                            {{ app.mailing_address_2 }}
                                                        </span>
                                                        <span class="d-block">
                                                            {{ app.mailing_city }}, {{ app.mailing_state }} {{ app.mailing_postal_code }}
                                                        </span>
                                                    </template>
                                                    <template v-else>
                                                        Same as above
                                                    </template>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Phone:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.phone_formatted }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Social Security Number:
                                                    </span>
                                                    <span class="d-block">
                                                        XXX-XX-{{ app.ssn_last_4 }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    {{ client.position_label_short }} Information
                                                </h5>
                                                <p v-for="job in app.jobs" :key="job.id">
                                                    <span class="d-block text-semibold">
                                                        {{ job.position.terminal_value }}
                                                    </span>
                                                    <span class="d-block">
                                                        Pay Type: {{ job.pay_type_pretty }}
                                                    </span>
                                                    <span class="d-block">
                                                        {{ job.pay_type == 'H' ? 'Per Hour' : 'Per Pay Period' }}:
                                                        ${{ job.pay_type == 'H' ? job.hourly_rate : job.salary_rate }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    Emergency Contacts
                                                </h5>
                                                <p v-for="contact in app.emergency_contacts" :key="contact.id">
                                                    <span class="d-block text-semibold">
                                                        {{ contact.full_name }}
                                                    </span>
                                                    <span class="d-block">
                                                        {{ contact.relationship }}
                                                    </span>
                                                    <span class="d-block" v-if="contact.phone">
                                                        {{ contact.phone_formatted }}
                                                    </span>
                                                    <span class="d-block" v-if="contact.email">
                                                        {{ contact.email }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    Equal Employment Opportunity Self-Identification
                                                </h5>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Legal Gender:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.gender == 'custom' ? 'Prefer not to say' : app.gender }}
                                                    </span>
                                                </p>
                                                <p>
                                                    <span class="d-block text-semibold">
                                                        Racial/Ethnic Identification:
                                                    </span>
                                                    <span class="d-block">
                                                        {{ app.ethnic_origin ? app.ethnic_origin : 'Prefer not to say' }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section" v-if="app.has_wotc_step">
                                                <h5 class="theading">
                                                    WOTC
                                                </h5>
                                                <p>
                                                    <span class="d-block" v-if="app.wotc_pre_qualified">
                                                        This employee <strong>may qualify</strong> for the Work Opportunity Tax Credit. You can check the application progress in HKP.
                                                    </span>
                                                    <span class="d-block" v-if="!app.wotc_pre_qualified">
                                                        This employee <strong>does not qualify</strong> for the Work Opportunity Tax Credit, based on their responses to the pre-screening questionnaire.
                                                    </span>

                                                    <a v-if="app.wotc_pdf_file" @click.prevent="showWOTCPDF(app)" :href="`${$api.API_BASE}/clients/${client.id}/onboarding-applications/${app.id}/wotc-pdf`" class="btn btn-outline-primary mt-1">View WOTC Responses</a>
                                                </p>
                                            </section>
                                            <section class="item-details-section" v-if="app.has_wotc2_step">
                                                <h5 class="theading">
                                                    WOTC
                                                </h5>
                                                <p>
                                                    <span class="d-block">
                                                        The WOTC questionnaire has been sent to the WOTC Provider (Hudson Group).
                                                    </span>

                                                    <a v-if="app.wotc_pdf_file" @click.prevent="showWOTCPDF(app)" :href="`${$api.API_BASE}/clients/${client.id}/onboarding-applications/${app.id}/wotc-pdf`" class="btn btn-outline-primary mt-1">View WOTC Responses</a>
                                                </p>
                                            </section>

                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    Required Documents
                                                </h5>
                                                <p v-for="form in app.forms" :key="form.id">
                                                    <span class="d-block text-semibold">
                                                        {{ form.form_name }}
                                                    </span>
                                                    <span class="d-block">
                                                        Completed on {{ $filters.date(form.filled_on) }}
                                                    </span>
                                                    <span class="d-block" v-if="form.signature_data.manager_signature">
                                                        Signed by {{ form.signature_data.manager_signature.full_name }}
                                                    </span>
                                                </p>
                                                <p v-for="formDoc in app.hr_form_documents" :key="formDoc.id">
                                                    <span class="d-block text-semibold">
                                                        {{ formDoc.hr_form_document.name }}
                                                    </span>
                                                    <span class="d-block">
                                                        Completed on {{ $filters.date(formDoc.filled_on) }}
                                                    </span>
                                                    <span class="d-block" v-if="formDoc.signature_data.manager_signature">
                                                        Signed by {{ formDoc.signature_data.manager_signature.full_name }}
                                                    </span>
                                                </p>
                                                <p v-for="doc in app.hr_documents" :key="doc.id">
                                                    <span class="d-block text-semibold">
                                                        {{ doc.hr_document.name }}
                                                    </span>
                                                    <span class="d-block">
                                                        Completed on {{ $filters.date(doc.signed_on) }}
                                                    </span>
                                                    <span class="d-block" v-if="doc.signature_data.manager_signature">
                                                        Signed by {{ doc.signature_data.manager_signature.full_name }}
                                                    </span>
                                                </p>
                                            </section>
                                            <section class="item-details-section">
                                                <h5 class="theading">
                                                    Direct Deposit
                                                </h5>
                                                <p v-for="acct in app.direct_deposit_accounts" :key="acct.id">
                                                    <span class="d-block text-semibold">
                                                        {{ acct.bank_name }}
                                                    </span>
                                                    <span class="d-block">
                                                        Routing Number: {{ acct.routing_number }}
                                                    </span>
                                                    <span class="d-block">
                                                        {{ acct.account_type.toUpperCase() }}
                                                    </span>
                                                    <span class="d-block" v-if="acct.split_type == 'percentage' && acct.percentage !== null">
                                                        {{ acct.percentage }}%
                                                    </span>
                                                    <span class="d-block" v-if="acct.split_type == 'remainder'">
                                                        Remainder
                                                    </span>
                                                    <span class="d-block" v-if="acct.split_type == 'fixed'">
                                                        ${{ acct.amount }}
                                                    </span>
                                                </p>
                                            </section>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                </div>

                <div>
                    <div class="info-widget-empty p-2" v-if="!onboardingApplications.length">
                        This employee has no completed onboarding applications.
                    </div>
                </div>

            </div>
        </slot>
    </info-widget>

    <approval-modal :client="client" :employee="employee" @updated="$emit('updated', $event)" ref="approvalModal" />
    <pdf-modal :url="currentPDF.url" :title="currentPDF.title" v-if="currentPDF" @close="currentPDF = null" ref="pdfModal" />
</template>

<script>
import ButtonRow from '@/components/ButtonRow'
import InfoWidget from '@/components/InfoWidget'
import PdfModal from '@/components/PdfModal'

import ApprovalModal from './ApprovalModal'

export default {
    components: {
        ButtonRow, InfoWidget, PdfModal,
        ApprovalModal
    },
    props: ['employee', 'client', 'onboardingApplicationId', 'onboardingApplications', 'expanded'],
    emits: ['resend-activation-email', 'edit-email', 'start-approval', 'updated', 'toggle_expand'],
    computed: {
        hasBouncedEmail() {
            if (!this.employee) {return false}
            return this.employee.warnings.filter(w => !w.resolved_on && w.warning_type == 'bounced-email').length > 0
        },
        canEditEmail() {
            if (this.employee.is_employee && !this.$permissions.hasRolesInCompany(this.client.id, this.employee.company_id, this.$store.state.security_roles.EMPLOYEE_ROLES)) {
                return false  // Onboarding-only managers can't edit existing employees' email addresses
            }
            return this.employee.is_employee || (this.employee.is_onboarding_application && this.employee.status != 'canceled')
        },
        openEditEmailModal() {
            this.$emit('edit-email')
        },
        approvalStarted() {
            if (!this.onboardingApplicationId) {return false}

            if (this.employee.hr_info_approved_on) {return true}
            if (this.employee.i9_approval_completed) {return true}
            if (this.employee.direct_deposit_approved_on) {return true}
            const formsApproved = this.employee.forms.filter((f) => {
                return f.accepted_on
            }).length

            if (formsApproved > 0) {return true}
            return false
        },
        workLocationIds() {
            if (!this.onboardingApplicationId) {return []}

            return Array.from(new Set(this.employee.jobs.map(j => j.position.work_location.id)))
        },
        positionIds() {
            if (!this.onboardingApplicationId) {return []}

            return Array.from(new Set(this.employee.jobs.map(j => j.position.id)))
        },
        userHasAccess() {
            if (!this.onboardingApplicationId) {return false}

            return this.$permissions.hasAccess(
                this.employee.company.client,
                this.employee.company.id,
                this.workLocationIds,
                this.positionIds,
            )
        },
    },
    data() {
        return {
            currentPDF: null,
            expandedOnboardingApplications: {},
        }
    },
    methods: {
        showWOTCPDF(app) {
            const doc = {
                name: 'WOTC',
                url: `${this.$api.API_BASE}/clients/${this.client.id}/onboarding-applications/${app.id}/wotc-pdf`
            }

            this.setCurrentPDF(doc)
        },
        setCurrentPDF(doc) {
            this.currentPDF = {
                title: doc.name,
                url: {
                    url: doc.url,
                    withCredentials: true,
                },
            }
        },
        startApproval() {
            this.$emit('start-approval')
            this.$bus.trackEvent(
                "Onboarding App Start Approval",
                {'app_id': this.onboardingApplicationId, 'client_id': this.client.id, 'company_id': this.employee.company.id}
            )
        },
        cancelOnboarding() {
            if (!this.onboardingApplicationId) {return}

            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                this.$api.patch(`/clients/${this.client.id}/onboarding-applications/${this.employee.id}`, {status: 'canceled'}).then((resp) => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$emit('updated', resp)
                }).catch((errors) => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.error)
                })
            }
        },
    },
}
</script>
